import React from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LanguageIcon from "@mui/icons-material/Language";
import { Box, Typography } from "@mui/material";
import { NATIVE_IOS_ANDROID_AVAILABLE } from "../../config/nativeAppsVisibility";
import { SUPPORT_FRENCH } from "../../config/supportFrench";
import { usePagesTranslation } from "../locales";

// Standard subscription feature keys (for translation: subs.feature.<key>.title / .desc)
export const SUBSCRIPTION_FEATURE_KEYS = [
    "dual-language",
    "word-networks",
    "smart-dictionary",
    "sliding-puzzle-quest",
    "monthly-ai-credits",
    "cloud-sync",
    "offline-play",
    "premium-exclusives"
];

export const EMPHASIZED_FEATURE_KEY = "dual-language";

// Legacy export for backward compatibility (SubscriptionSuccess still uses raw comparison)
export const SUBSCRIPTION_FEATURES = [
    "Full Access to the Entire Puzzles",
    "Deep Word Networks",
    "Smart Dictionary Integration",
    "Your Puzzle Assistant",
    "Cloud-Sync Progress",
    "Offline Play",
    "Premium Exclusives"
];
export const EMPHASIZED_FEATURE = "Full Access to the Entire Puzzles";

/**
 * Web Premium catalog — productKey / priceKey must match server Stripe config
 * (docs/stripe/test-product.md, docs/stripe/prod-products.md).
 * Promo copy: docs/stripe/promotional.txt → subs.plan.<key>.promo
 */
export const SUBSCRIPTION_PLANS = {
    starter_fun_bundle: {
        name: "Starter Fun Bundle",
        price: "$3.99",
        period: "one-time",
        productKey: "web_premium",
        priceKey: "starter_fun_bundle",
        unlocksPremium: true,
        popular: true,
        bestValue: true,
        /** Puzzles included in this collection unlock. */
        puzzlesIncluded: 550,
        /** Play credits granted on purchase (webhook). */
        aiCreditsPack: 550,
        aiCreditsSplitLines: true,
        /** Bonus puzzle coins granted on purchase (webhook). */
        bonusPuzzleCoins: 200,
    },
    classic_challenge: {
        name: "Classic Challenge",
        price: "$1.99",
        period: "one-time",
        productKey: "web_premium",
        priceKey: "classic_challenge",
        unlocksPremium: true,
        puzzlesIncluded: 300,
        aiCreditsPack: 300,
        aiCreditsSplitLines: true,
        bonusPuzzleCoins: 200,
    },
    pack300: {
        name: "300 Coins",
        price: "$0.99",
        period: "pack",
        productKey: "web_premium",
        priceKey: "pack300",
        aiCreditsPack: 300,
        bonusPuzzleCoins: 300,
        neverExpires: true
    },
    tutor_standard_monthly: {
        name: "Monthly",
        price: "$4.99",
        period: "month",
        productKey: "tutor_standard",
        priceKey: "tutor_standard_monthly",
    },
    tutor_standard_yearly: {
        name: "Yearly",
        price: "$49.00",
        period: "year",
        productKey: "tutor_standard",
        priceKey: "tutor_standard_yearly",
        popular: true,
        bestValue: true,
        savings: "Save vs monthly",
    },
    tutor_extra_pdf_50: {
        name: "Extra 50 PDF Exports",
        price: "$4.99",
        period: "one-time",
        productKey: "tutor_extra_pdf",
        priceKey: "tutor_extra_pdf_50",
        successPath: "/tutors/settings",
    },
};

/** Display order: unlocks row, then coin/credit packs. (Free layer is not a Stripe SKU — see product-policy Free layer.) */
export const SUBSCRIPTION_PLAN_PICKER_ORDER = [
    "starter_fun_bundle",
    "classic_challenge",
    "pack300",
];

export const DEFAULT_SELECTED_PLAN = "starter_fun_bundle";

/** Default selection on `/tutors` offer pickers. */
export const DEFAULT_TUTOR_PLAN = "tutor_standard_yearly";

/** One-time Extra 50 PDF Exports pack. */
export const TUTOR_EXTRA_PDF_PLAN = "tutor_extra_pdf_50";

export const TUTOR_STANDARD_PLAN_PICKER_ORDER = [
    "tutor_standard_monthly",
    "tutor_standard_yearly",
];

const TUTOR_PLAN_KEY_SET = new Set([
    ...TUTOR_STANDARD_PLAN_PICKER_ORDER,
    "tutor_extra_pdf_50",
]);

export function isTutorsCatalogPath(pathname) {
    const path = String(pathname || "").replace(/\/+$/, "") || "/";
    return path === "/tutors" || path.startsWith("/tutors/");
}

export function getSubscriptionCatalogFromPath(pathname) {
    return isTutorsCatalogPath(pathname) ? "tutors" : "web";
}

/** Default selection for paid catalog pickers. */
export const SUBSCRIPTION_PICKER_FALLBACK_PLAN = "starter_fun_bundle";

/** Legacy multi-month keys (no longer offered). */
export const SUBSCRIPTION_MULTI_MONTH_PLAN_KEYS = ["quarterly", "semiannual", "annual"];

/**
 * @param {boolean} [hideMultiMonthPlans]
 * @returns {string}
 */
export function getSubscriptionPickerFallbackPlan(hideMultiMonthPlans = false) {
    return SUBSCRIPTION_PICKER_FALLBACK_PLAN;
}

/**
 * Plan entries for subscription picker UIs (paid Stripe offers only).
 * @param {{ hideMultiMonthPlans?: boolean }} [options]
 */
export function getSubscriptionPlanPickerEntries({ hideMultiMonthPlans = false, catalog = "web" } = {}) {
    if (catalog === "tutors") {
        return TUTOR_STANDARD_PLAN_PICKER_ORDER
            .filter((key) => SUBSCRIPTION_PLANS[key])
            .map((key) => [key, SUBSCRIPTION_PLANS[key]]);
    }
    const orderedKeys = SUBSCRIPTION_PLAN_PICKER_ORDER.filter((key) => SUBSCRIPTION_PLANS[key]);
    const leftoverKeys = Object.keys(SUBSCRIPTION_PLANS).filter(
        (key) => !SUBSCRIPTION_PLAN_PICKER_ORDER.includes(key) && !TUTOR_PLAN_KEY_SET.has(key),
    );
    let entries = [...orderedKeys, ...leftoverKeys].map((key) => [key, SUBSCRIPTION_PLANS[key]]);
    if (hideMultiMonthPlans) {
        entries = entries.filter(([key]) => !SUBSCRIPTION_MULTI_MONTH_PLAN_KEYS.includes(key));
    }
    return entries;
}

/** User-facing plan label (Premium branding; server/Stripe still use Standard keys). */
export function getSubscriptionPlanLabel(t, planKey) {
    if (!planKey) return "";
    const premiumKey = `subs.plan.${planKey}.premium-name`;
    const premium = t(premiumKey);
    if (premium && premium !== premiumKey) return premium;
    const nameKey = `subs.plan.${planKey}.name`;
    const name = t(nameKey);
    if (name && name !== nameKey) return name;
    return SUBSCRIPTION_PLANS[planKey]?.name || planKey;
}

/**
 * Resolve a plan copy field with graceful fallbacks (never return a raw i18n key).
 * @param {(key: string) => string} t
 * @param {string} planKey
 * @param {string} field
 * @param {string} [fallback]
 */
export function getSubscriptionPlanField(t, planKey, field, fallback = "") {
    if (!planKey || !field) return fallback;
    const key = `subs.plan.${planKey}.${field}`;
    const val = t(key);
    if (val && val !== key) return val;
    return fallback;
}

/** Map Stripe recurring price metadata to {@link SUBSCRIPTION_PLANS} key. */
export function stripePriceToSubscriptionPlanKey(price) {
    if (!price) return null;
    const count = Number(price.intervalCount);
    const interval = String(price.interval || "").toLowerCase();
    if (interval !== "month") return null;
    if (count === 1) return "monthly";
    if (count === 3) return "quarterly";
    if (count === 6) return "semiannual";
    if (count === 12) return "annual";
    return null;
}

/** Stripe price card title for change-plan dialogs. */
export function getStripePricePlanLabel(t, price) {
    const planKey = stripePriceToSubscriptionPlanKey(price);
    if (planKey) return getSubscriptionPlanLabel(t, planKey);
    if (!price) return "";
    const interval = String(price.interval || "");
    const unit = interval ? interval.charAt(0).toUpperCase() + interval.slice(1) : "";
    return `${price.intervalCount}-${unit} Premium`;
}

/** Stripe recurring prices: keep 1-month Standard only (exclude 3/6/12-month bundles). */
export function filterMonthlyOnlyStripePrices(prices) {
    if (!Array.isArray(prices)) return [];
    return prices.filter((p) => p.interval === "month" && p.intervalCount === 1);
}

/** Omits cloud-sync / offline-play until native apps ship (see nativeAppsVisibility). */
export function getVisibleSubscriptionFeatureKeys({ hideSlidingPuzzleQuest = false } = {}) {
    let keys = SUBSCRIPTION_FEATURE_KEYS;
    if (!SUPPORT_FRENCH) {
        keys = keys.filter((k) => k !== "dual-language");
    }
    if (hideSlidingPuzzleQuest) {
        keys = keys.filter((k) => k !== "sliding-puzzle-quest");
    }
    if (NATIVE_IOS_ANDROID_AVAILABLE) return keys;
    return keys.filter((k) => k !== "cloud-sync" && k !== "offline-play");
}

function FeatureBlock({ featureKey, emphasized }) {
    const { t } = usePagesTranslation();
    const title = t(`subs.feature.${featureKey}.title`);
    const desc = t(`subs.feature.${featureKey}.desc`);
    /** Highlight bilingual + AI assistant titles in brand green (matches sparkle / clues UI). */
    const titleGreen = emphasized || featureKey === "monthly-ai-credits";
    return (
        <Box>
            <Typography
                variant="subtitle1"
                component="h3"
                sx={{
                    fontWeight: 600,
                    color: titleGreen ? "#3BA666" : "text.primary",
                    fontSize: "1rem",
                    lineHeight: 1.35,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75
                }}
            >
                {featureKey === EMPHASIZED_FEATURE_KEY && (
                    <LanguageIcon sx={{ fontSize: "1.35rem", flexShrink: 0 }} />
                )}
                {featureKey === "monthly-ai-credits" && (
                    <AutoAwesomeIcon sx={{ fontSize: "1.35rem", flexShrink: 0 }} />
                )}
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.65 }}>
                {desc}
            </Typography>
        </Box>
    );
}

/** Shown once below plan cards (not repeated on every Standard tier card). */
export function StandardMonthlyCreditsNote({ sx = {} }) {
    const { t } = usePagesTranslation();
    return (
        <Box sx={{ textAlign: "center", width: "100%", ...sx }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {t("subs.plan.standard-monthly-credits-note")}
            </Typography>
        </Box>
    );
}

/**
 * Shared offer extras on plan cards: AI credits, bonus coins, unlock, badges.
 * @param {{ planKey: string, plan: object }} props
 */
export function PlanCardOfferExtras({ planKey, plan, hideBestValue = false }) {
    const { t } = usePagesTranslation();
    if (!plan) {
        return null;
    }
    return (
        <>
            {plan.aiCreditsPack != null && (
                plan.aiCreditsSplitLines ? (
                    <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                            {getSubscriptionPlanField(
                                t,
                                planKey,
                                "credits-amount",
                                `${Number(plan.aiCreditsPack).toLocaleString()} Credits`,
                            )}
                        </Typography>
                        {(() => {
                            const detail = getSubscriptionPlanField(t, planKey, "credits-detail", "");
                            return detail ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {detail}
                                </Typography>
                            ) : null;
                        })()}
                    </Box>
                ) : (
                    <Typography variant="body2" color="primary.main" sx={{ mt: 0.5, fontWeight: 600 }}>
                        {getSubscriptionPlanField(
                            t,
                            planKey,
                            "ai-credits",
                            `${Number(plan.aiCreditsPack).toLocaleString()} Coins`,
                        )}
                    </Typography>
                )
            )}
            {plan.bonusPuzzleCoins != null && plan.bonusPuzzleCoins > 0 && plan.aiCreditsPack !== plan.bonusPuzzleCoins && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                        {getSubscriptionPlanField(
                            t,
                            planKey,
                            "coins-bonus",
                            `${Number(plan.bonusPuzzleCoins).toLocaleString()} Coins`,
                        )}
                    </Typography>
                    {(() => {
                        const detail = getSubscriptionPlanField(t, planKey, "coins-detail", "");
                        return detail ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {detail}
                            </Typography>
                        ) : null;
                    })()}
                </Box>
            )}
            {plan.unlocksPremium && plan.puzzlesIncluded !== plan.aiCreditsPack && (
                <Typography variant="body2" color="primary.main" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {getSubscriptionPlanField(
                        t,
                        planKey,
                        "unlock",
                        plan.puzzlesIncluded != null
                            ? `${Number(plan.puzzlesIncluded).toLocaleString()} puzzles you can play`
                            : "Unlock premium puzzles",
                    )}
                </Typography>
            )}
            {plan.bestValue && !hideBestValue && (
                <Typography variant="caption" color="success.main" sx={{ fontWeight: "bold", display: "block", mt: 0.5 }}>
                    {getSubscriptionPlanField(
                        t,
                        planKey,
                        "best-value",
                        t("subs.dialog.most-popular"),
                    )}
                </Typography>
            )}
            {plan.savings && (
                <Typography variant="caption" color="success.main" sx={{ fontWeight: "bold", display: "block", mt: 0.5 }}>
                    {getSubscriptionPlanField(t, planKey, "savings", "")}
                </Typography>
            )}
            {(plan.period === "month" || plan.period === "year") && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {getSubscriptionPlanField(
                        t,
                        planKey,
                        "period",
                        plan.period === "year" ? "per year" : "per month",
                    )}
                </Typography>
            )}
        </>
    );
}

/** Promo blurb under offer cards (docs/stripe/promotional.txt). */
export function PlanCardPromoNote({ planKey, sx = {} }) {
    const { t } = usePagesTranslation();
    if (!planKey) {
        return null;
    }
    const promo = t(`subs.plan.${planKey}.promo`);
    if (!promo || promo === `subs.plan.${planKey}.promo`) {
        return null;
    }
    return (
        <Box sx={{ mt: 1, textAlign: "center", ...sx }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                {promo}
            </Typography>
        </Box>
    );
}

/** Plan details shown once below the card row (period, credits, savings). */
/** Plan details shown once below the card row (policy note). Free / unlock bonuses render on cards. */
export function PlanPickerFootnotes({ planKeys, catalog = "web", sx = {} }) {
    const { t } = usePagesTranslation();
    if (catalog === "tutors") {
        return (
            <Box sx={{ mt: 2, textAlign: "center", width: "100%", ...sx }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {t("subs.terms.renew")}
                </Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ mt: 2, textAlign: "center", width: "100%", ...sx }}>
            <StandardMonthlyCreditsNote />
        </Box>
    );
}

/**
 * Premium plan feature list with optional App Store badge (profile, subscription pickers).
 * @param {{ titleKey?: string, hideSlidingPuzzleQuest?: boolean }} props
 */
export function PremiumPlanFeaturesSection({
    titleKey = "subs.dialog.premium-features",
    hideSlidingPuzzleQuest = false,
}) {
    const { t } = usePagesTranslation();
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                {t(titleKey)}
            </Typography>
            <PanelPlan hideSlidingPuzzleQuest={hideSlidingPuzzleQuest} />
        </Box>
    );
}

/**
 * Shared component for rendering subscription features list
 */
export const PanelPlan = ({ hideSlidingPuzzleQuest = false }) => {
    const keys = getVisibleSubscriptionFeatureKeys({ hideSlidingPuzzleQuest });
    const slidingIdx = keys.indexOf("sliding-puzzle-quest");
    const splitAt = slidingIdx >= 0 ? slidingIdx + 1 : Math.ceil(keys.length / 2);
    return (
        <Box sx={{ display: "flex", gap: { xs: 0, md: 4 }, pt: 1, flexWrap: "wrap" }}>
            <Box sx={{ flex: { xs: "1 1 100%", md: 1 }, minWidth: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {keys.slice(0, splitAt).map((featureKey) => (
                        <FeatureBlock
                            key={featureKey}
                            featureKey={featureKey}
                            emphasized={featureKey === EMPHASIZED_FEATURE_KEY}
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", md: 1 }, minWidth: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {keys.slice(splitAt).map((featureKey) => (
                        <FeatureBlock
                            key={featureKey}
                            featureKey={featureKey}
                            emphasized={featureKey === EMPHASIZED_FEATURE_KEY}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
